import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/ui/PageHeader';
import { ProfileForm } from '@/components/ui/ProfileForm';
import { PasswordChangeForm } from '@/components/ui/PasswordChangeForm';
import { apiFetch, ApiError } from '@/lib/api';
import type { Profile as ProfileType } from '@/lib/db/schema';

export function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/profile');
      const data = await res.json();
      setProfile(data.profile);
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to load profile. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-white mb-2">Something went wrong</h2>
          <p className="text-neutral-400 text-sm mb-6">{error}</p>
          <button
            onClick={fetchProfile}
            className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white rounded-lg text-sm hover:bg-neutral-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <PageHeader title="Profile Settings" />

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Profile Section */}
        <section className="bg-neutral-900 border border-neutral-800 rounded-lg p-6">
          <h2 className="text-lg font-medium text-white mb-4">Profile Information</h2>
          {profile && <ProfileForm profile={profile} />}
        </section>

        {/* Account Section */}
        <section className="bg-neutral-900 border border-neutral-800 rounded-lg p-6">
          <h2 className="text-lg font-medium text-white mb-4">Account</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Email</label>
              <p className="text-white">{user?.email}</p>
            </div>
          </div>
        </section>

        {/* Password Section */}
        <section className="bg-neutral-900 border border-neutral-800 rounded-lg p-6">
          <h2 className="text-lg font-medium text-white mb-4">Change Password</h2>
          <PasswordChangeForm />
        </section>

        {/* Danger Zone */}
        <section className="bg-neutral-900 border border-red-900/50 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-400 mb-4">Danger Zone</h2>
          <p className="text-sm text-neutral-400 mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <button
            disabled
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg text-sm opacity-50 cursor-not-allowed"
          >
            Delete Account (Coming Soon)
          </button>
        </section>
      </main>
    </div>
  );
}
